<template>
  <div class="depenses-manager">
    <!-- Header Premium -->
    <div class="header-section">
      <div class="header-content">
        <div class="header-info">
          <div class="icon-wrapper">
            <i class="fas fa-receipt"></i>
          </div>
          <div class="header-text">
            <h2>💸 Gestion des Dépenses</h2>
            <p>Dépenses journalières et charges mensuelles</p>
          </div>
        </div>
        <div class="header-stats">
          <div class="stat-card">
            <div class="stat-value">{{ formatCurrency(totalDepenses) }}</div>
            <div class="stat-label">Total Dépenses</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ depenses.length }}</div>
            <div class="stat-label">Dépenses</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Onglets -->
    <div class="tabs-section">
      <button 
        @click="activeTab = 'journalieres'"
        :class="['tab-btn', { active: activeTab === 'journalieres' }]"
      >
        <i class="fas fa-calendar-day"></i>
        Dépenses Journalières
      </button>
      <button 
        @click="activeTab = 'mensuelles'"
        :class="['tab-btn', { active: activeTab === 'mensuelles' }]"
      >
        <i class="fas fa-calendar-alt"></i>
        Charges Mensuelles
      </button>
      <button 
        @click="activeTab = 'validation'"
        :class="['tab-btn', { active: activeTab === 'validation' }]"
        v-if="userRole === 'Financier' || userRole === 'Admin'"
      >
        <i class="fas fa-check-circle"></i>
        Validation
        <span v-if="depensesEnAttente > 0" class="badge">{{ depensesEnAttente }}</span>
      </button>
    </div>

    <!-- Contenu Dépenses Journalières -->
    <div v-if="activeTab === 'journalieres'" class="tab-content">
      <!-- Filtres -->
      <div class="filters-section">
        <div class="filter-group">
          <!-- Filtre par technicien (visible pour Admin et Financier) -->
          <div v-if="userRole === 'Admin' || userRole === 'Financier'" class="premium-filter-wrapper filter-technicien">
            <div class="filter-icon-container">
              <i class="fas fa-user-cog"></i>
            </div>
            <select 
              v-model="filters.technicien" 
              class="premium-select"
              @change="loadDepenses"
            >
              <option value="">👨‍🔧 Tous les techniciens</option>
              <option 
                v-for="tech in techniciens" 
                :key="tech._id" 
                :value="tech._id"
              >
                {{ tech.prenom }} {{ tech.nom }}
              </option>
            </select>
            <div class="filter-arrow">
              <i class="fas fa-chevron-down"></i>
            </div>
          </div>

          <div class="premium-filter-wrapper">
            <div class="filter-icon-container">
              <i class="fas fa-tags"></i>
            </div>
            <select v-model="filters.categorieJournaliere" class="premium-select">
              <option value="">🏷️ Toutes catégories</option>
              <option value="carburant">⛽ Carburant</option>
              <option value="peage">🛣️ Péage</option>
              <option value="divers">📋 Divers</option>
            </select>
            <div class="filter-arrow">
              <i class="fas fa-chevron-down"></i>
            </div>
          </div>

          <div class="premium-filter-wrapper">
            <div class="filter-icon-container">
              <i class="fas fa-calendar-alt"></i>
            </div>
            <input 
              v-model="filters.dateDebut" 
              type="date" 
              class="premium-date"
            >
          </div>

          <button @click="loadDepenses" class="btn-refresh">
            <i class="fas fa-sync-alt"></i>
            Actualiser
          </button>

          <button @click="showCreateModal = true" class="btn-create">
            <i class="fas fa-plus"></i>
            Nouvelle Dépense
          </button>
        </div>
      </div>

      <!-- Liste des dépenses journalières -->
      <div class="depenses-grid">
        <div 
          v-for="depense in depensesJournalieres" 
          :key="depense._id"
          class="depense-card"
          :class="getDepenseStatusClass(depense.statut)"
        >
          <div class="card-header">
            <div class="depense-info">
              <h4>{{ getCategorieLabel(depense.categorieJournaliere) }}</h4>
              <p>{{ depense.designation }}</p>
            </div>
            <div class="montant">
              {{ formatCurrency(depense.montant) }}
            </div>
          </div>

          <div class="card-body">
            <div class="info-row">
              <span class="label">Date :</span>
              <span class="value">{{ formatDate(depense.date) }}</span>
            </div>
            <div class="info-row">
              <span class="label">Demandeur :</span>
              <span class="value">{{ depense.demandeur?.prenom }} {{ depense.demandeur?.nom }}</span>
            </div>
            <div class="info-row">
              <span class="label">Mode paiement :</span>
              <span class="value">{{ getModePaiementLabel(depense.modePaiement) }}</span>
            </div>
            <div class="info-row">
              <span class="label">Statut :</span>
              <span class="value badge" :class="getStatutClass(depense.statut)">{{ depense.statut }}</span>
            </div>
          </div>

          <div class="card-actions">
            <button @click="viewDetails(depense)" class="btn-details">
              <i class="fas fa-eye"></i>
              Détails
            </button>
            <button v-if="depense.pieceJointe" @click="viewPieceJointe(depense)" class="btn-photo">
              <i class="fas fa-paperclip"></i>
              Justificatif
            </button>
          </div>
        </div>
      </div>

      <!-- État vide -->
      <div v-if="!loading && depensesJournalieres.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-receipt"></i>
        </div>
        <h3>Aucune dépense journalière</h3>
        <p>Créez votre première dépense journalière</p>
        <button @click="showCreateModal = true" class="btn-create-empty">
          <i class="fas fa-plus"></i>
          Nouvelle Dépense
        </button>
      </div>
    </div>

    <!-- Contenu Charges Mensuelles -->
    <div v-if="activeTab === 'mensuelles'" class="tab-content">
      <div class="charges-grid">
        <div 
          v-for="charge in chargesMensuelles" 
          :key="charge._id"
          class="charge-card-premium"
          :class="getDepenseStatusClass(charge.statut)"
        >
          <!-- Header Premium -->
          <div class="charge-header-premium">
            <div class="charge-icon-section">
              <div class="charge-icon-wrapper">
                <i :class="getCategorieIcon(charge.categorieMensuelle)"></i>
              </div>
              <div class="charge-info-premium">
                <h4>{{ getCategorieLabel(charge.categorieMensuelle) }}</h4>
                <p>{{ charge.designation || 'Charge mensuelle' }}</p>
              </div>
            </div>
            <div class="montant-premium">
              {{ formatCurrency(charge.montant) }}
            </div>
          </div>

          <!-- Body Premium -->
          <div class="charge-body-premium">
            <div class="charge-details-grid">
              <div class="detail-item">
                <div class="detail-icon">
                  <i class="fas fa-sync-alt"></i>
                </div>
                <div class="detail-content">
                  <span class="detail-label">Fréquence</span>
                  <span class="detail-value">{{ charge.recurrence?.frequence || 'Unique' }}</span>
                </div>
              </div>

              <div v-if="charge.recurrence?.prochaineEcheance" class="detail-item">
                <div class="detail-icon">
                  <i class="fas fa-calendar-check"></i>
                </div>
                <div class="detail-content">
                  <span class="detail-label">Prochaine échéance</span>
                  <span class="detail-value">{{ formatDate(charge.recurrence.prochaineEcheance) }}</span>
                </div>
              </div>

              <div class="detail-item">
                <div class="detail-icon">
                  <i class="fas fa-info-circle"></i>
                </div>
                <div class="detail-content">
                  <span class="detail-label">Statut</span>
                  <span class="detail-value">
                    <span class="status-badge-premium" :class="getStatutClass(charge.statut)">
                      {{ getStatutLabel(charge.statut) }}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="charge-actions-premium">
              <button @click="viewDetails(charge)" class="btn-details-premium">
                <i class="fas fa-eye"></i>
                Détails
              </button>
              <button v-if="charge.pieceJointe" @click="viewPieceJointe(charge)" class="btn-document-premium">
                <i class="fas fa-paperclip"></i>
                Justificatif
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- État vide -->
      <div v-if="!loading && chargesMensuelles.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-calendar-alt"></i>
        </div>
        <h3>Aucune charge mensuelle</h3>
        <p>Créez votre première charge mensuelle</p>
        <button @click="showCreateModal = true" class="btn-create-empty">
          <i class="fas fa-plus"></i>
          Nouvelle Charge
        </button>
      </div>
    </div>

    <!-- Contenu Validation (Financier) -->
    <div v-if="activeTab === 'validation' && (userRole === 'Financier' || userRole === 'Admin')" class="tab-content">
      <div class="validation-grid">
        <div 
          v-for="depense in depensesEnValidation" 
          :key="depense._id"
          class="validation-card"
        >
          <div class="card-header">
            <div class="depense-info">
              <h4>{{ getCategorieLabel(depense.categorieJournaliere || depense.categorieMensuelle) }}</h4>
              <p>{{ depense.designation }}</p>
            </div>
            <div class="montant">
              {{ formatCurrency(depense.montant) }}
            </div>
          </div>

          <div class="card-body">
            <div class="info-row">
              <span class="label">Demandeur :</span>
              <span class="value">{{ depense.demandeur?.prenom }} {{ depense.demandeur?.nom }}</span>
            </div>
            <div class="info-row">
              <span class="label">Date :</span>
              <span class="value">{{ formatDate(depense.date) }}</span>
            </div>
            <div class="info-row">
              <span class="label">Type :</span>
              <span class="value">{{ depense.type === 'journaliere' ? 'Journalière' : 'Mensuelle' }}</span>
            </div>
          </div>

          <div class="card-actions">
            <button @click="validerDepense(depense._id, 'Validée')" class="btn-valider">
              <i class="fas fa-check"></i>
              Valider
            </button>
            <button @click="validerDepense(depense._id, 'Rejetée')" class="btn-rejeter">
              <i class="fas fa-times"></i>
              Rejeter
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Chargement des dépenses...</p>
    </div>

    <!-- Modal Création Dépense -->
    <CreateDepenseModal 
      v-if="showCreateModal"
      @close="showCreateModal = false"
      @depense-created="handleDepenseCreated"
    />

    <!-- Modal Détails Dépense -->
    <div v-if="showDetailsModal" class="modal-overlay" @click="closeDetailsModal">
      <div class="modal-container" @click.stop>
        <!-- Header -->
        <div class="modal-header">
          <div class="header-content">
            <div class="icon-wrapper">
              <i class="fas fa-receipt"></i>
            </div>
            <div class="header-text">
              <h3>Détails de la Dépense</h3>
              <p>Informations complètes</p>
            </div>
          </div>
          <button @click="closeDetailsModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Contenu -->
        <div class="modal-body" v-if="selectedDepense">
          <!-- Carte Héro -->
          <div class="hero-card">
            <div class="montant-section">
              <div class="montant-principal">{{ formatCurrency(selectedDepense.montant) }}</div>
              <div class="type-depense">{{ selectedDepense.type === 'journaliere' ? 'Dépense Journalière' : 'Charge Mensuelle' }}</div>
            </div>
            <div class="date-section">
              <div class="date-principale">{{ formatDate(selectedDepense.date) }}</div>
              <div class="statut-badge" :class="getStatutClass(selectedDepense.statut)">
                {{ getStatutLabel(selectedDepense.statut) }}
              </div>
            </div>
          </div>

          <!-- Informations Générales -->
          <div class="info-section">
            <h4><i class="fas fa-info-circle"></i> Informations Générales</h4>
            <div class="info-grid">
              <div class="info-item">
                <span class="label">Désignation :</span>
                <span class="value">{{ selectedDepense.designation || 'Non renseigné' }}</span>
              </div>
              <div class="info-item">
                <span class="label">Catégorie :</span>
                <span class="value">{{ getCategorieLabel(selectedDepense.categorieJournaliere || selectedDepense.categorieMensuelle || selectedDepense.categorie) }}</span>
              </div>
              <div class="info-item">
                <span class="label">Fournisseur :</span>
                <span class="value">{{ selectedDepense.fournisseur || 'Non renseigné' }}</span>
              </div>
              <div class="info-item">
                <span class="label">Mode de paiement :</span>
                <span class="value">{{ getModePaiementLabel(selectedDepense.modePaiement) }}</span>
              </div>
              <div v-if="selectedDepense.numeroFacture" class="info-item">
                <span class="label">N° Facture :</span>
                <span class="value">{{ selectedDepense.numeroFacture }}</span>
              </div>
            </div>
          </div>

          <!-- Récurrence (si applicable) -->
          <div v-if="selectedDepense.recurrence?.actif" class="info-section">
            <h4><i class="fas fa-sync-alt"></i> Récurrence</h4>
            <div class="info-grid">
              <div class="info-item">
                <span class="label">Fréquence :</span>
                <span class="value">{{ selectedDepense.recurrence.frequence }}</span>
              </div>
              <div class="info-item">
                <span class="label">Jour d'échéance :</span>
                <span class="value">{{ selectedDepense.recurrence.jourEcheance }}</span>
              </div>
            </div>
          </div>

          <!-- Commentaires -->
          <div v-if="selectedDepense.commentaires" class="info-section">
            <h4><i class="fas fa-comment"></i> Commentaires</h4>
            <div class="commentaires-content">
              {{ selectedDepense.commentaires }}
            </div>
          </div>

          <!-- Pièce Jointe -->
          <div v-if="selectedDepense.pieceJointe" class="info-section">
            <h4><i class="fas fa-paperclip"></i> Justificatif</h4>
            <div class="piece-jointe-container">
              <img 
                v-if="isImage(selectedDepense.pieceJointe)"
                :src="selectedDepense.pieceJointe" 
                alt="Justificatif"
                class="piece-jointe-image"
                @click="openPieceJointeModal"
              >
              <div v-else class="piece-jointe-file" @click="openPieceJointeModal">
                <i class="fas fa-file-pdf"></i>
                <span>Cliquez pour voir le justificatif</span>
              </div>
              <p class="piece-jointe-hint">Cliquez pour agrandir</p>
            </div>
          </div>

          <!-- Informations Système -->
          <div class="info-section">
            <h4><i class="fas fa-cog"></i> Informations Système</h4>
            <div class="info-grid">
              <div class="info-item">
                <span class="label">Demandeur :</span>
                <span class="value">{{ selectedDepense.demandeur?.nom }} {{ selectedDepense.demandeur?.prenom }}</span>
              </div>
              <div class="info-item">
                <span class="label">Date création :</span>
                <span class="value">{{ formatDateTime(selectedDepense.dateCreation) }}</span>
              </div>
              <div v-if="selectedDepense.validePar" class="info-item">
                <span class="label">Validé par :</span>
                <span class="value">{{ selectedDepense.validePar.nom }} {{ selectedDepense.validePar.prenom }}</span>
              </div>
              <div v-if="selectedDepense.dateValidation" class="info-item">
                <span class="label">Date validation :</span>
                <span class="value">{{ formatDateTime(selectedDepense.dateValidation) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button @click="closeDetailsModal" class="btn-secondary">
            <i class="fas fa-times"></i>
            Fermer
          </button>
          <button 
            v-if="selectedDepense?.pieceJointe" 
            @click="openPieceJointeModal" 
            class="btn-primary"
          >
            <i class="fas fa-paperclip"></i>
            Voir Justificatif
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Pièce Jointe -->
    <div v-if="showPieceJointeModal" class="piece-jointe-modal-overlay" @click="closePieceJointeModal">
      <div class="piece-jointe-modal-container" @click.stop>
        <button @click="closePieceJointeModal" class="piece-jointe-close-btn">
          <i class="fas fa-times"></i>
        </button>
        <img 
          v-if="selectedDepense && isImage(selectedDepense.pieceJointe)"
          :src="selectedDepense.pieceJointe" 
          alt="Justificatif"
          class="piece-jointe-modal-image"
        >
        <iframe
          v-else-if="selectedDepense && selectedDepense.pieceJointe"
          :src="selectedDepense.pieceJointe"
          class="piece-jointe-modal-pdf"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useToast } from 'vue-toastification'
import CreateDepenseModal from './CreateDepenseModal.vue'
import jsPDF from 'jspdf'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Capacitor } from '@capacitor/core'

// Fonction utilitaire pour télécharger PDF sur mobile
async function downloadPDFMobile(doc, fileName) {
  try {
    const isNative = Capacitor.isNativePlatform()
    if (isNative) {
      const base64Data = doc.output('datauristring').split(',')[1]
      const result = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Documents,
        recursive: true
      })
      return { success: true, message: `Reçu téléchargé dans Documents/${fileName}`, path: result.uri }
    } else {
      doc.save(fileName)
      return { success: true, message: 'Reçu téléchargé avec succès' }
    }
  } catch (error) {
    console.error('Erreur téléchargement PDF:', error)
    try {
      const pdfBlob = doc.output('blob')
      const blobUrl = URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000)
      return { success: true, message: 'Reçu téléchargé' }
    } catch (e) {
      return { success: false, message: 'Erreur lors du téléchargement' }
    }
  }
}

export default {
  name: 'DepensesManager',
  components: {
    CreateDepenseModal
  },
  props: {
    userRole: String,
    userId: String
  },
  setup(props) {
    const toast = useToast()
    const loading = ref(false)
    const depenses = ref([])
    const activeTab = ref('journalieres')
    const showCreateModal = ref(false)
    const showDetailsModal = ref(false)
    const showPieceJointeModal = ref(false)
    const selectedDepense = ref(null)
    
    const filters = ref({
      categorieJournaliere: '',
      dateDebut: '',
      dateFin: '',
      technicien: ''
    })
    
    // Liste des techniciens pour le filtre
    const techniciens = ref([])

    const totalDepenses = computed(() => {
      return depenses.value.reduce((total, dep) => total + (dep.montant || 0), 0)
    })

    const depensesJournalieres = computed(() => {
      return depenses.value.filter(dep => dep.type === 'journaliere')
    })

    const chargesMensuelles = computed(() => {
      return depenses.value.filter(dep => dep.type === 'mensuelle')
    })

    const depensesEnValidation = computed(() => {
      return depenses.value.filter(dep => dep.statut === 'Soumise')
    })

    const depensesEnAttente = computed(() => {
      return depensesEnValidation.value.length
    })

    const loadDepenses = async () => {
      loading.value = true
      try {
        const token = localStorage.getItem('token')
        const params = new URLSearchParams()
        
        if (filters.value.categorieJournaliere) params.append('categorieJournaliere', filters.value.categorieJournaliere)
        if (filters.value.dateDebut) params.append('dateDebut', filters.value.dateDebut)
        if (filters.value.dateFin) params.append('dateFin', filters.value.dateFin)
        if (filters.value.technicien) params.append('demandeur', filters.value.technicien)
        
        console.log('=== CHARGEMENT DÉPENSES FRONTEND ===')
        console.log('URL:', `/api/finance/depenses?${params}`)
        
        const response = await fetch(`/api/finance/depenses?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        console.log('Réponse status:', response.status)
        
        if (response.ok) {
          const data = await response.json()
          console.log('Dépenses reçues:', data)
          depenses.value = data.depenses || []
        } else {
          const error = await response.json()
          console.error('Erreur chargement:', error)
          // Erreur silencieuse - ne pas afficher de toast
          depenses.value = []
        }
      } catch (error) {
        console.error('Erreur:', error)
        // Erreur silencieuse - ne pas afficher de toast
        depenses.value = []
      } finally {
        loading.value = false
      }
    }

    const validerDepense = async (depenseId, statut) => {
      try {
        const token = localStorage.getItem('token')
        const motifRejet = statut === 'Rejetée' ? prompt('Motif du rejet :') : null
        
        if (statut === 'Rejetée' && !motifRejet) return

        const response = await fetch(`/api/finance/depenses/${depenseId}/valider`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            statut,
            motifRejet,
            commentairesFinancier: ''
          })
        })

        if (response.ok) {
          toast.success(`✅ Dépense ${statut.toLowerCase()}`)
          loadDepenses()
        } else {
          const error = await response.json()
          toast.error(error.message || 'Erreur lors de la validation')
        }
      } catch (error) {
        console.error('Erreur validation:', error)
        toast.error('Erreur lors de la validation')
      }
    }

    const handleDepenseCreated = () => {
      showCreateModal.value = false
      loadDepenses()
    }

    const viewDetails = (depense) => {
      console.log('🔍 Debug - Dépense sélectionnée:', depense)
      console.log('🔍 Debug - Catégorie journalière:', depense.categorieJournaliere)
      console.log('🔍 Debug - Catégorie mensuelle:', depense.categorieMensuelle)
      console.log('🔍 Debug - Catégorie générale:', depense.categorie)
      console.log('🔍 Debug - Désignation:', depense.designation)
      console.log('🔍 Debug - Mode paiement:', depense.modePaiement)
      console.log('🔍 Debug - Toutes les clés:', Object.keys(depense))
      selectedDepense.value = depense
      showDetailsModal.value = true
    }

    const viewPieceJointe = (depense) => {
      selectedDepense.value = depense
      showPieceJointeModal.value = true
    }

    const closeDetailsModal = () => {
      showDetailsModal.value = false
      selectedDepense.value = null
    }

    const openPieceJointeModal = () => {
      showPieceJointeModal.value = true
    }

    const closePieceJointeModal = () => {
      showPieceJointeModal.value = false
    }

    const isImage = (base64String) => {
      if (!base64String) return false
      return base64String.startsWith('data:image/')
    }

    const getModePaiementLabel = (mode) => {
      if (!mode) return 'Non renseigné'
      
      const modes = {
        'especes': '💵 Espèces',
        'cash_societe': '💵 Cash société',
        'avance_personnel': '👤 Avance personnelle',
        'cheque': '📝 Chèque',
        'carte_bancaire': '💳 Carte Bancaire',
        'virement': '🏦 Virement',
        'prelevement': '📋 Prélèvement'
      }
      return modes[mode] || mode
    }

    const getStatutClass = (statut) => {
      const classes = {
        'Soumise': 'statut-soumise',
        'Validee': 'statut-validee',
        'Validée': 'statut-validee', // Avec accent
        'Rejetee': 'statut-rejetee',
        'Rejetée': 'statut-rejetee', // Avec accent
        'Payee': 'statut-payee',
        'Payée': 'statut-payee', // Avec accent
        'Remboursee': 'statut-remboursee',
        'Remboursée': 'statut-remboursee' // Avec accent
      }
      return classes[statut] || 'statut-default'
    }

    const getStatutLabel = (statut) => {
      const labels = {
        'Soumise': '⏳ En attente',
        'Validee': '✅ Validée',
        'Validée': '✅ Validée', // Avec accent
        'Rejetee': '❌ Rejetée',
        'Rejetée': '❌ Rejetée', // Avec accent
        'Payee': '💰 Payée',
        'Payée': '💰 Payée', // Avec accent
        'Remboursee': '💸 Remboursée',
        'Remboursée': '💸 Remboursée' // Avec accent
      }
      return labels[statut] || statut
    }

    const formatDateTime = (date) => {
      if (!date) return 'Non renseigné'
      return new Date(date).toLocaleString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND'
      }).format(amount || 0)
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('fr-FR')
    }

    const getCategorieLabel = (categorie) => {
      if (!categorie) return 'Non renseigné'
      
      const labels = {
        // Journalières
        'carburant': '⛽ Carburant',
        'peage': '🛣️ Péage',
        'divers': '📋 Divers',
        // Mensuelles
        'loyer': '🏢 Loyer',
        'internet': '🌐 Internet',
        'telephone': '📞 Téléphone',
        'hebergement_vps': '☁️ Hébergement VPS',
        'salaires': '💼 Salaires',
        'assurances': '🛡️ Assurances',
        'autre': '📋 Autre'
      }
      return labels[categorie] || categorie
    }

    const getCategorieIcon = (categorie) => {
      const icons = {
        // Journalières
        'carburant': 'fas fa-gas-pump',
        'parking': 'fas fa-parking',
        'peage': 'fas fa-road',
        'repas': 'fas fa-utensils',
        'achat_urgent': 'fas fa-shopping-cart',
        // Mensuelles
        'loyer': 'fas fa-building',
        'internet': 'fas fa-wifi',
        'telephone': 'fas fa-phone',
        'hebergement_vps': 'fas fa-cloud',
        'salaires': 'fas fa-briefcase',
        'assurances': 'fas fa-shield-alt',
        'autre': 'fas fa-file-alt'
      }
      return icons[categorie] || 'fas fa-file-alt'
    }

    const getDepenseStatusClass = (statut) => {
      return `depense-${statut?.toLowerCase()?.replace('é', 'e')}`
    }

    const exportRecuPDF = async (depense) => {
      try {
        // Calculer la hauteur estimée du contenu
        let estimatedHeight = 10 // Marge top
        estimatedHeight += 25 // En-tête
        estimatedHeight += 15 // Titre et numéro
        estimatedHeight += 10 // Demandeur
        estimatedHeight += 30 // Détails (variable selon contenu)
        if (depense.fournisseur) estimatedHeight += 4
        estimatedHeight += 20 // Total
        estimatedHeight += 20 // Footer
        estimatedHeight += 5 // Marge bottom
        
        // Format ticket de caisse : 80mm de largeur, hauteur dynamique
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [80, estimatedHeight]
        })
        const pageWidth = 80
        let y = 10

        // ========== EN-TÊTE TICKET ==========
        doc.setTextColor(0, 0, 0)
        doc.setFontSize(16)
        doc.setFont('helvetica', 'bold')
        doc.text('AMANA', pageWidth / 2, y, { align: 'center' })
        y += 5
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.text('MANAGEMENT SUITE', pageWidth / 2, y, { align: 'center' })
        y += 3
        doc.text('Tunis, Tunisie', pageWidth / 2, y, { align: 'center' })
        y += 3
        // Afficher les 5 derniers chiffres du téléphone du demandeur ou un numéro par défaut
        const telDemandeur = depense.demandeur?.telephone || ''
        const telAffiche = telDemandeur ? `Tel: +216 ${telDemandeur.slice(-8)}` : 'Tel: +216 XX XXX XXX'
        doc.text(telAffiche, pageWidth / 2, y, { align: 'center' })
        y += 5

        // Ligne de séparation
        doc.setDrawColor(0, 0, 0)
        doc.setLineWidth(0.3)
        doc.line(5, y, pageWidth - 5, y)
        y += 5

        // ========== TITRE REÇU ==========
        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.text('REÇU DE CAISSE', pageWidth / 2, y, { align: 'center' })
        y += 5

        // Ligne de séparation
        doc.line(5, y, pageWidth - 5, y)
        y += 5

        // Numéro de reçu et date
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        const numeroRecu = depense._id?.substring(depense._id.length - 8).toUpperCase() || 'N/A'
        const dateRecu = formatDate(new Date())
        doc.text(`N° ${numeroRecu}`, pageWidth / 2, y, { align: 'center' })
        y += 4
        doc.text(`Date: ${dateRecu}`, pageWidth / 2, y, { align: 'center' })
        y += 5

        // Ligne de séparation
        doc.line(5, y, pageWidth - 5, y)
        y += 5

        // ========== INFORMATIONS DEMANDEUR ==========
        doc.setTextColor(0, 0, 0)
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        const nomDemandeur = `${depense.demandeur?.prenom || ''} ${depense.demandeur?.nom || 'Non renseigné'}`
        doc.text(`Demandeur: ${nomDemandeur}`, 5, y)
        y += 4
        doc.text(`Date: ${formatDate(depense.date)}`, 5, y)
        y += 5

        // Ligne de séparation
        doc.line(5, y, pageWidth - 5, y)
        y += 5

        // ========== DÉTAILS DE LA DÉPENSE ==========
        doc.setTextColor(0, 0, 0)
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')

        // Désignation
        doc.text(`Designation:`, 5, y)
        y += 4
        doc.setFont('helvetica', 'bold')
        doc.text(depense.designation || 'Non renseigné', 5, y)
        doc.setFont('helvetica', 'normal')
        y += 5

        // Catégorie (sans emojis pour PDF)
        const categorie = getCategorieLabel(depense.categorieJournaliere || depense.categorieMensuelle || depense.categorie).replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '').trim()
        doc.text(`Categorie: ${categorie}`, 5, y)
        y += 4

        // Fournisseur
        if (depense.fournisseur) {
          doc.text(`Fournisseur: ${depense.fournisseur}`, 5, y)
          y += 4
        }

        // Mode de paiement (sans emojis pour PDF)
        const modePaiement = getModePaiementLabel(depense.modePaiement).replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '').trim()
        doc.text(`Paiement: ${modePaiement}`, 5, y)
        y += 4

        // Statut (sans emojis pour PDF)
        const statut = getStatutLabel(depense.statut).replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '').trim()
        doc.text(`Statut: ${statut}`, 5, y)
        y += 6

        // Ligne de séparation
        doc.line(5, y, pageWidth - 5, y)
        y += 5

        // ========== MONTANT TOTAL ==========
        doc.setTextColor(0, 0, 0)
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.text('TOTAL:', 5, y)
        doc.setFontSize(14)
        doc.text(formatCurrency(depense.montant), pageWidth - 5, y, { align: 'right' })
        y += 6

        // Double ligne de séparation
        doc.setLineWidth(0.5)
        doc.line(5, y, pageWidth - 5, y)
        y += 1
        doc.line(5, y, pageWidth - 5, y)
        y += 6

        // ========== FOOTER ==========
        doc.setTextColor(0, 0, 0)
        doc.setFontSize(7)
        doc.setFont('helvetica', 'italic')
        doc.text('Merci de votre confiance', pageWidth / 2, y, { align: 'center' })
        y += 4
        doc.setFont('helvetica', 'normal')
        doc.text('Amana Management Suite', pageWidth / 2, y, { align: 'center' })
        y += 6

        // Code-barres simulé
        doc.setDrawColor(0, 0, 0)
        doc.setLineWidth(0.3)
        for (let i = 0; i < 30; i++) {
          const x = 10 + i * 2
          const height = Math.random() > 0.5 ? 2 : 4
          doc.line(x, y, x, y + height)
        }
        y += 6
        doc.setFontSize(7)
        doc.text(numeroRecu, pageWidth / 2, y, { align: 'center' })

        // Télécharger le PDF avec Capacitor Filesystem (compatible mobile)
        const fileName = `recu_depense_${depense._id?.substring(depense._id.length - 8) || 'export'}_${formatDate(new Date()).replace(/\//g, '-')}.pdf`
        const result = await downloadPDFMobile(doc, fileName)
        
        if (result.success) {
          toast.success(`✅ ${result.message}`)
          console.log('✅ Reçu PDF généré:', fileName)
        } else {
          toast.error(result.message)
        }
      } catch (error) {
        console.error('❌ Erreur export reçu PDF:', error)
        toast.error('Erreur lors de l\'export du reçu')
      }
    }

    // Charger la liste des techniciens pour le filtre
    const loadTechniciens = async () => {
      if (props.userRole !== 'Admin' && props.userRole !== 'Financier') return
      
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/users?role=Technicien&limit=100', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        if (response.ok) {
          const data = await response.json()
          techniciens.value = data.utilisateurs || data.users || []
          console.log('👨‍🔧 Techniciens chargés pour dépenses:', techniciens.value.length)
        }
      } catch (error) {
        console.error('Erreur chargement techniciens:', error)
      }
    }

    onMounted(() => {
      console.log('✅ DepensesManager monté - exportRecuPDF disponible:', typeof exportRecuPDF)
      loadDepenses()
      loadTechniciens()
    })

    return {
      loading,
      depenses,
      activeTab,
      showCreateModal,
      showDetailsModal,
      showPieceJointeModal,
      selectedDepense,
      filters,
      techniciens,
      totalDepenses,
      depensesJournalieres,
      chargesMensuelles,
      depensesEnValidation,
      depensesEnAttente,
      loadDepenses,
      loadTechniciens,
      validerDepense,
      handleDepenseCreated,
      viewDetails,
      viewPieceJointe,
      closeDetailsModal,
      openPieceJointeModal,
      closePieceJointeModal,
      isImage,
      formatCurrency,
      formatDate,
      formatDateTime,
      getCategorieLabel,
      getCategorieIcon,
      getModePaiementLabel,
      getStatutClass,
      getStatutLabel,
      getDepenseStatusClass,
      exportRecuPDF
    }
  }
}
</script>

<style scoped>
.depenses-manager {
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

/* Header Section */
.header-section {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  color: white;
  position: relative;
  overflow: hidden;
}

.header-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.icon-wrapper {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  font-size: 1.8rem;
}

.header-text h2 {
  margin: 0 0 0.5rem 0;
  font-size: 1.8rem;
  font-weight: 700;
}

.header-stats {
  display: flex;
  gap: 2rem;
}

.stat-card {
  text-align: center;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1rem 1.5rem;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

/* Onglets */
.tabs-section {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid rgba(0, 27, 183, 0.1);
}

.tab-btn {
  padding: 1rem 2rem;
  border: none;
  background: transparent;
  color: #6c757d;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 3px solid transparent;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  position: relative;
}

.tab-btn.active {
  color: var(--primary-blue);
  border-bottom-color: var(--primary-blue);
  background: rgba(0, 27, 183, 0.05);
}

.badge {
  background: #ef4444;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
}

/* Filtres Premium */
.filters-section {
  background: rgba(248, 250, 252, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.filter-group {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.premium-filter-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 2px solid rgba(226, 232, 240, 0.8);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 200px;
}

.filter-technicien {
  min-width: 250px;
}

.filter-technicien .premium-select {
  min-width: 180px;
}

.premium-filter-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.15);
  background: rgba(255, 255, 255, 0.95);
}

.filter-icon-container {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.9rem;
}

.premium-select,
.premium-date {
  flex: 1;
  border: none;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  background: transparent;
  outline: none;
  appearance: none;
}

.filter-arrow {
  padding: 0 1rem;
  color: var(--secondary-blue);
  pointer-events: none;
}

.btn-refresh,
.btn-create {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-create {
  background: linear-gradient(135deg, #10b981, #059669);
}

/* Grids */
.depenses-grid,
.charges-grid,
.validation-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

.depense-card,
.charge-card,
.validation-card {
  background: white;
  border-radius: 16px;
  border: 1px solid rgba(0, 27, 183, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.depense-card:hover,
.charge-card:hover,
.validation-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 27, 183, 0.15);
}

.card-header {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05), rgba(0, 70, 255, 0.03));
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.depense-info h4,
.charge-info h4 {
  margin: 0 0 0.25rem 0;
  color: var(--primary-blue);
  font-weight: 600;
}

.montant {
  font-size: 1.5rem;
  font-weight: 700;
  color: #ef4444;
}

.card-body {
  padding: 1.5rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.label {
  font-weight: 600;
  color: #6c757d;
  font-size: 0.9rem;
}

.value {
  font-weight: 500;
  color: #1a202c;
  text-align: right;
  flex: 1;
  margin-left: 1rem;
}

.card-actions {
  padding: 1rem 1.5rem;
  background: rgba(248, 250, 252, 0.5);
  display: flex;
  gap: 0.75rem;
}

.btn-details,
.btn-photo,
.btn-valider,
.btn-rejeter,
.btn-export-recu {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.btn-details {
  background: var(--primary-blue);
  color: white;
}

.btn-photo {
  background: #10b981;
  color: white;
}

.btn-valider {
  background: #10b981;
  color: white;
}

.btn-rejeter {
  background: #ef4444;
  color: white;
}

/* Status badges */
.status-pending { background: #fed7aa; color: #ea580c; }
.status-validated { background: #d1fae5; color: #065f46; }
.status-rejected { background: #fecaca; color: #dc2626; }
.status-paid { background: #e0e7ff; color: #3730a3; }

/* États vides et loading */
.empty-state,
.loading-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #6c757d;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.btn-create-empty {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1rem 2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 27, 183, 0.1);
  border-left: 4px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
  }
  
  .filter-group {
    flex-direction: column;
    align-items: stretch;
  }
  
  .premium-filter-wrapper {
    min-width: auto;
  }
  
  .depenses-grid,
  .charges-grid,
  .validation-grid {
    grid-template-columns: 1fr;
  }
}

/* Styles pour les modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.modal-container {
  background: white;
  border-radius: 20px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
}

.modal-header {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  padding: 2rem;
  border-radius: 20px 20px 0 0;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  position: relative;
  z-index: 1;
}

.header-text {
  text-align: left;
}

.icon-wrapper {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  font-size: 1.8rem;
}

.header-text h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.8rem;
  font-weight: 700;
}

.header-text p {
  margin: 0;
  opacity: 0.9;
}

.close-btn {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  z-index: 2;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.modal-body {
  padding: 2rem;
}

.hero-card {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05), rgba(0, 70, 255, 0.03));
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(0, 27, 183, 0.1);
}

.montant-principal {
  font-size: 2.5rem;
  font-weight: 700;
  color: #10b981;
  margin-bottom: 0.5rem;
}

.type-depense {
  font-size: 1.1rem;
  color: var(--primary-blue);
  font-weight: 600;
}

.date-principale {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--primary-blue);
  text-align: right;
  margin-bottom: 0.5rem;
}

.statut-badge {
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  min-width: 100px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Badges de statut dans les cartes - forme circulaire */
.badge.statut-soumise,
.badge.statut-validee,
.badge.statut-rejetee,
.badge.statut-payee,
.badge.statut-remboursee {
  width: auto;
  height: auto;
  min-width: 80px;
  padding: 0.4rem 0.8rem;
  border-radius: 25px;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.statut-soumise {
  background: linear-gradient(135deg, #fff3cd, #ffeaa7);
  color: #856404;
  border: 1px solid #ffc107;
}

.statut-validee {
  background: linear-gradient(135deg, #28a745, #20c997) !important;
  color: #ffffff !important;
  border: 1px solid #28a745;
}

/* Spécificité renforcée pour les badges de statut validé */
.badge.statut-validee,
.statut-badge.statut-validee {
  background: linear-gradient(135deg, #28a745, #20c997) !important;
  color: #ffffff !important;
  border: 1px solid #28a745;
}

.statut-rejetee {
  background: linear-gradient(135deg, #f8d7da, #f1b0b7);
  color: #721c24;
  border: 1px solid #dc3545;
}

.statut-payee, .statut-remboursee {
  background: linear-gradient(135deg, #d1ecf1, #b3d9e0);
  color: #0c5460;
  border: 1px solid #17a2b8;
}

/* ===== DESIGN PREMIUM CHARGES MENSUELLES ===== */
.charge-card-premium {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(0, 27, 183, 0.08);
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 32px rgba(0, 27, 183, 0.08);
  position: relative;
}

.charge-card-premium::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--primary-blue), var(--secondary-blue));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.charge-card-premium:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 60px rgba(0, 27, 183, 0.15);
  border-color: rgba(0, 27, 183, 0.2);
}

.charge-card-premium:hover::before {
  opacity: 1;
}

.charge-header-premium {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.03), rgba(0, 70, 255, 0.02));
  padding: 1.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 27, 183, 0.05);
}

.charge-icon-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.charge-icon-wrapper {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.4rem;
  box-shadow: 0 4px 16px rgba(0, 27, 183, 0.2);
}

.charge-info-premium h4 {
  margin: 0 0 0.25rem 0;
  color: var(--primary-blue);
  font-weight: 700;
  font-size: 1.1rem;
}

.charge-info-premium p {
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
  opacity: 0.8;
}

.montant-premium {
  font-size: 1.6rem;
  font-weight: 800;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 4px rgba(239, 68, 68, 0.1);
}

.charge-body-premium {
  padding: 1.75rem;
}

.charge-details-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(248, 250, 252, 0.6);
  border-radius: 12px;
  border: 1px solid rgba(0, 27, 183, 0.04);
  transition: all 0.3s ease;
}

.detail-item:hover {
  background: rgba(0, 27, 183, 0.02);
  border-color: rgba(0, 27, 183, 0.08);
}

.detail-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.1), rgba(0, 70, 255, 0.05));
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-blue);
  font-size: 0.9rem;
}

.detail-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-label {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 0.95rem;
  color: #1e293b;
  font-weight: 600;
}

.status-badge-premium {
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
}

.charge-actions-premium {
  display: flex;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 27, 183, 0.05);
}

.btn-details-premium,
.btn-document-premium {
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-details-premium {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.2);
}

.btn-details-premium:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 27, 183, 0.3);
}

.btn-document-premium {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}

.btn-document-premium:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
}

/* Responsive pour charges premium */
@media (max-width: 768px) {
  .charge-header-premium {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .charge-icon-section {
    flex-direction: column;
    text-align: center;
  }
  
  .montant-premium {
    font-size: 1.4rem;
  }
  
  .charge-actions-premium {
    flex-direction: column;
  }
}

.info-section {
  background: rgba(248, 250, 252, 0.5);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(0, 27, 183, 0.05);
}

.info-section h4 {
  color: var(--primary-blue);
  margin: 0 0 1rem 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.info-grid {
  display: grid;
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.label {
  font-weight: 600;
  color: #6c757d;
  min-width: 150px;
}

.value {
  flex: 1;
  text-align: right;
  color: #1a202c;
  font-weight: 500;
}

.commentaires-content {
  background: white;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid rgba(0, 27, 183, 0.1);
  line-height: 1.6;
}

.piece-jointe-container {
  text-align: center;
}

.piece-jointe-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.3s ease;
  border: 2px solid rgba(0, 27, 183, 0.1);
}

.piece-jointe-image:hover {
  transform: scale(1.02);
}

.piece-jointe-file {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  border: 2px solid rgba(0, 27, 183, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
}

.piece-jointe-file:hover {
  background: rgba(0, 27, 183, 0.05);
  transform: translateY(-2px);
}

.piece-jointe-file i {
  font-size: 3rem;
  color: #dc3545;
  margin-bottom: 1rem;
}

.piece-jointe-hint {
  margin: 0.5rem 0 0 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.modal-footer {
  padding: 1.5rem 2rem;
  border-top: 1px solid rgba(0, 27, 183, 0.1);
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.btn-secondary, .btn-primary {
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
  transform: translateY(-2px);
}

.btn-primary {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.3);
}

/* Modal Pièce Jointe */
.piece-jointe-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.piece-jointe-modal-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.piece-jointe-modal-image {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
}

.piece-jointe-modal-pdf {
  width: 80vw;
  height: 80vh;
  border: none;
  border-radius: 8px;
}

.piece-jointe-close-btn {
  position: absolute;
  top: -50px;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.piece-jointe-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

/* Responsive pour modals */
@media (max-width: 768px) {
  .hero-card {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  
  .date-principale {
    text-align: center;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .value {
    text-align: left;
  }
  
  .modal-footer {
    flex-direction: column;
  }
}

/* Boutons Export Reçu - Styles spécifiques */
.btn-export-recu {
  background: linear-gradient(135deg, #dc2626, #ef4444) !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.2) !important;
  position: relative;
  overflow: hidden;
}

.btn-export-recu-premium {
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #dc2626, #ef4444);
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.2);
  position: relative;
  overflow: hidden;
  flex: 1;
}

.btn-export-recu::before,
.btn-export-recu-premium::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.6s ease;
}

.btn-export-recu:hover,
.btn-export-recu-premium:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.35);
  background: linear-gradient(135deg, #b91c1c, #dc2626);
}

.btn-export-recu:hover::before,
.btn-export-recu-premium:hover::before {
  left: 100%;
}

.btn-export-recu:active,
.btn-export-recu-premium:active {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.25);
}

.btn-export-recu i,
.btn-export-recu-premium i {
  font-size: 1rem;
}

@media (max-width: 768px) {
  .btn-export-recu,
  .btn-export-recu-premium {
    width: 100%;
    justify-content: center;
    padding: 0.7rem 1rem;
  }
}

/* ========== RESPONSIVE MOBILE COMPLET ========== */
@media (max-width: 768px) {
  .depenses-manager {
    padding: 0.5rem;
  }
  
  /* Header responsive */
  .header-section {
    padding: 1rem;
    border-radius: 12px;
    margin-bottom: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .header-info {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }
  
  .icon-wrapper {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
    margin: 0 auto;
  }
  
  .header-text h2 {
    font-size: 1.3rem;
  }
  
  .header-text p {
    font-size: 0.85rem;
  }
  
  .header-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    width: 100%;
  }
  
  .stat-card {
    padding: 0.75rem;
  }
  
  .stat-value {
    font-size: 1.2rem;
  }
  
  .stat-label {
    font-size: 0.75rem;
  }
  
  /* Onglets responsive */
  .tabs-section {
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0.5rem;
    background: #f8fafc;
    border-radius: 12px;
    border-bottom: none;
    margin-bottom: 1rem;
  }
  
  .tab-btn {
    flex: 1;
    min-width: calc(50% - 0.25rem);
    padding: 0.75rem 0.5rem;
    font-size: 0.7rem;
    border-radius: 8px;
    border-bottom: none;
    justify-content: center;
    text-align: center;
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .tab-btn i {
    font-size: 1rem;
  }
  
  .tab-btn.active {
    background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
    color: white;
  }
  
  .tab-btn .badge {
    position: absolute;
    top: -5px;
    right: -5px;
    font-size: 0.6rem;
    padding: 0.1rem 0.3rem;
  }
  
  /* Filtres responsive */
  .filters-section {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }
  
  .filter-group {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .premium-filter-wrapper {
    width: 100%;
  }
  
  .btn-refresh,
  .btn-create {
    width: 100%;
    justify-content: center;
  }
  
  /* Grille des dépenses responsive */
  .depenses-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .depense-card {
    border-radius: 12px;
  }
  
  .depense-card .card-header {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
    padding: 1rem;
  }
  
  .depense-card .montant {
    font-size: 1.25rem;
    align-self: flex-end;
  }
  
  .depense-card .card-body {
    padding: 1rem;
  }
  
  .depense-card .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    padding: 0.5rem 0;
  }
  
  .depense-card .card-actions {
    flex-direction: column;
    padding: 1rem;
    gap: 0.5rem;
  }
  
  .btn-details,
  .btn-photo {
    width: 100%;
    justify-content: center;
  }
  
  /* Charges mensuelles responsive */
  .charges-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .charge-card-premium {
    border-radius: 12px;
  }
  
  .charge-header-premium {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
    padding: 1rem;
  }
  
  .charge-icon-section {
    width: 100%;
  }
  
  .montant-premium {
    font-size: 1.25rem;
    align-self: flex-end;
  }
  
  .charge-body-premium {
    padding: 1rem;
  }
  
  .charge-details-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  
  .detail-item {
    padding: 0.75rem;
  }
  
  .charge-actions-premium {
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;
  }
  
  .btn-details-premium,
  .btn-document-premium {
    width: 100%;
    justify-content: center;
  }
  
  /* Validation grid responsive */
  .validation-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .validation-card .card-header {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
  }
  
  .validation-card .card-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .btn-valider,
  .btn-rejeter {
    width: 100%;
  }
  
  /* Modal responsive */
  .modal-container {
    width: 95%;
    max-width: none;
    margin: 1rem;
    max-height: 90vh;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .modal-header h3 {
    font-size: 1.1rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  
  .piece-jointe-image {
    max-height: 200px;
  }
  
  /* Empty state responsive */
  .empty-state {
    padding: 2rem 1rem;
  }
  
  .empty-icon {
    font-size: 2.5rem;
  }
  
  .empty-state h3 {
    font-size: 1.1rem;
  }
  
  .empty-state p {
    font-size: 0.9rem;
  }
  
  .btn-create-empty {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .depenses-manager {
    padding: 0.25rem;
  }
  
  .header-section {
    padding: 0.75rem;
  }
  
  .header-text h2 {
    font-size: 1.1rem;
  }
  
  .stat-value {
    font-size: 1rem;
  }
  
  .tabs-section {
    padding: 0.25rem;
  }
  
  .tab-btn {
    padding: 0.5rem 0.25rem;
    font-size: 0.65rem;
  }
  
  .tab-btn i {
    font-size: 0.9rem;
  }
  
  .depense-card .card-header,
  .depense-card .card-body,
  .depense-card .card-actions {
    padding: 0.75rem;
  }
  
  .depense-card .montant {
    font-size: 1.1rem;
  }
  
  .charge-header-premium,
  .charge-body-premium,
  .charge-actions-premium {
    padding: 0.75rem;
  }
}
</style>
